import client from "./client";

// ─────────────────────────── plans / prices ───────────────────────────

export interface PlanInfo {
  key: string;
  /** Legacy alias — some older screens read `.name` directly. */
  name: string;
  display_name: string;
  seat_limit: number;
  monthly_price_usd: number;
  /** Null for Free + Enterprise ("contact us" pricing). */
  per_seat_price_usd: number | null;
  /** Post-launch standard rate. Currently $18 for Starter and
   *  $32 for Professional — surfaced as a strike-through under the
   *  founding rate. Null when no standard rate is defined yet. */
  standard_per_seat_price_usd: number | null;
  features: string[];
  purchasable: boolean;
}

interface RawPlanInfo {
  key: string;
  display_name: string;
  seat_limit: number;
  monthly_price_usd: number;
  per_seat_price_usd: number | null;
  standard_per_seat_price_usd?: number | null;
  features: string[];
  purchasable: boolean;
}

function normalizePlan(p: RawPlanInfo): PlanInfo {
  return {
    ...p,
    name: p.display_name,
    standard_per_seat_price_usd: p.standard_per_seat_price_usd ?? null,
  };
}

// ─────────────────────────── subscription state ───────────────────────────

export interface SubscriptionState {
  plan: string;
  plan_display_name: string;
  seat_limit: number;
  seats_used: number;
  seats_available: number | null;
  /** Canonical Stripe status — alias of ``subscription_status``. */
  status: string | null;
  subscription_status: string | null;
  current_period_end: string | null;
  trial_ends_at: string | null;
  has_stripe_customer: boolean;
  has_active_subscription: boolean;
  /** Synthesized from ``has_active_subscription`` for older UI paths. */
  stripe_subscription_id: string | null;
  /** Per-seat price for the current plan ($/employee/month). Null on
   *  Free and Enterprise — those tiers don't have per-seat pricing. */
  per_seat_price_usd: number | null;
}

interface RawSubscriptionState {
  plan: string;
  plan_display_name: string;
  seat_limit: number;
  seats_used: number;
  seats_available: number | null;
  subscription_status: string | null;
  current_period_end: string | null;
  trial_ends_at: string | null;
  has_stripe_customer: boolean;
  has_active_subscription: boolean;
  per_seat_price_usd: number | null;
}

function normalizeSubscription(s: RawSubscriptionState): SubscriptionState {
  return {
    ...s,
    status: s.subscription_status,
    stripe_subscription_id: s.has_active_subscription ? "active" : null,
    per_seat_price_usd: s.per_seat_price_usd ?? null,
  };
}

// ─────────────────────────── checkout / portal ───────────────────────────

export interface CheckoutResponse {
  /** Stripe-hosted URL to redirect the browser to. */
  url: string;
  /** Legacy field — aliased to ``checkout_url`` for older screens. */
  checkout_url: string;
  session_id: string;
  plan: string;
}

export interface PortalResponse {
  url: string;
}

// Backend returns ``checkout_url``; older callers read ``.url``.
interface RawCheckoutResponse {
  checkout_url: string;
  session_id: string;
  plan: string;
}

// ─────────────────────────── endpoints ───────────────────────────

export async function listPlans(): Promise<PlanInfo[]> {
  const res = await client.get<RawPlanInfo[]>("/billing/plans");
  return res.data.map(normalizePlan);
}

/** Newer alias — identical to ``listPlans()``. */
export async function listPrices(): Promise<PlanInfo[]> {
  const res = await client.get<RawPlanInfo[]>("/billing/prices");
  return res.data.map(normalizePlan);
}

export async function getSubscription(): Promise<SubscriptionState> {
  const res = await client.get<RawSubscriptionState>("/billing/subscription");
  return normalizeSubscription(res.data);
}

/** Newer alias — identical to ``getSubscription()``. */
export async function getBillingStatus(): Promise<SubscriptionState> {
  const res = await client.get<RawSubscriptionState>("/billing/status");
  return normalizeSubscription(res.data);
}

export async function createCheckout(
  planKey: string,
  opts?: { quantity?: number; priceId?: string }
): Promise<CheckoutResponse> {
  // Accept the older signature ``createCheckout("starter")`` and the newer
  // ``createCheckout(planKey, { quantity, priceId })`` shape used by the
  // per-seat upgrade flow.
  const body: Record<string, unknown> = { plan: planKey, plan_key: planKey };
  if (opts?.quantity != null) body.quantity = opts.quantity;
  if (opts?.priceId) body.price_id = opts.priceId;
  const res = await client.post<RawCheckoutResponse>("/billing/checkout", body);
  return {
    url: res.data.checkout_url,
    checkout_url: res.data.checkout_url,
    session_id: res.data.session_id,
    plan: res.data.plan,
  };
}

export async function createPortal(): Promise<PortalResponse> {
  const res = await client.post<PortalResponse>("/billing/portal", {});
  return res.data;
}

// ─────────────────────────── plan features ───────────────────────────

/**
 * Feature bundle returned by ``GET /billing/features``. ``modules`` is the
 * string ``"all"`` (no gating) or an explicit list of module keys to render.
 * Every other field is a boolean capability flag.
 */
export interface PlanFeatures {
  plan: string;
  subscription_status: string | null;
  is_trialing: boolean;
  /**
   * Internal/founder org — bypasses all billing gates. The frontend
   * uses this to suppress trial/upgrade banners and treat the org as
   * if it were on the Enterprise tier.
   */
  is_internal?: boolean;
  /**
   * Trial window has lapsed and no paid subscription was selected.
   * Frontend renders the "Choose a plan" lockout screen — only the
   * /settings/billing route remains usable.
   */
  is_trial_expired?: boolean;
  seat_limit: number; // 0 = unlimited
  modules: "all" | string[];
  ai_agents: boolean;
  recruiting: boolean;
  performance: boolean;
  complaints: boolean;
  advanced_reports: boolean;
  workflows: boolean;
  compliance: boolean;
  audit_logs: boolean;
  sso: boolean;
  /** Self-serve Telegram-bot provisioning (Professional + Enterprise + trial). */
  telegram_bot: boolean;
  /** Invoicing module (Starter+). */
  invoices: boolean;
  /** Asset/equipment management (Starter+). */
  assets: boolean;
  /** 1:1 meetings (Starter+). Independent of full performance reviews. */
  one_on_ones: boolean;
  /** AI chat assistant (Starter+). */
  ai_assistant: boolean;
  /** Onboarding/offboarding lifecycle automations (Starter+). */
  lifecycle: boolean;
  /** Engagement / pulse surveys + eNPS dashboard (Pro+). */
  pulse_surveys: boolean;
  /** Lenavio Learn — training course library + LMS (Pro+). */
  training: boolean;
  /** Lenavio Connect — Slack integration with HR slash commands (Pro+). */
  slack_integration: boolean;
  /** Lenavio Talent Map — skills library, gap analysis, dev plans (Pro+). */
  talent_map: boolean;
  /** Lenavio Succession — key positions + bench planning (Pro+). */
  succession: boolean;
  /** Lenavio Comp — AI compensation benchmarking + pay equity (Enterprise+). */
  compensation: boolean;
  /** Lenavio Expense — expense management with manager approval (Pro+). */
  expenses: boolean;
  /** Lenavio Forecast — workforce planning + AI scenarios (Enterprise+). */
  workforce_planning: boolean;
  /** Lenavio Predict — flight risk, turnover forecasts, AI insights (Enterprise+). */
  predictive_analytics: boolean;
  /** Lenavio Connect — Microsoft Teams bot + adaptive cards (Enterprise+). */
  teams_integration: boolean;
  /** SMS notifications via Twilio (Pro+). */
  sms_notifications: boolean;
  /**
   * Command bar can fire mutating ``action_*`` tools.
   *   * Starter — False (command bar is read-only: queries + charts
   *     + navigation only).
   *   * Professional+ — True (admins can onboard, terminate, enroll
   *     directly from the bar).
   * Drives the engine-side tool-catalog filter; SPA reads this to
   * show "AI write actions" badges on plan cards.
   */
  ai_write_actions: boolean;
  /** Enterprise-only legal agents (termination / hiring / worker
   *  classification / investigation / wage-hour / regulatory). */
  legal_agents: boolean;
  /** Enterprise-only Practice Profile (AI learns the customer's
   *  policies; settings page hidden when False). */
  practice_profile: boolean;
  /** Enterprise-only DEI analytics dashboards. */
  dei_analytics: boolean;
}

export async function getPlanFeatures(): Promise<PlanFeatures> {
  const res = await client.get<PlanFeatures>("/billing/features");
  return res.data;
}

// ─────────────────────────── AI credits ────────────────────────────

/**
 * Read-only snapshot of an org's AI credit balance. Returned by
 * ``GET /billing/credits``. Visible to HR_ADMIN + SUPER_ADMIN.
 *
 *   * ``monthly_remaining`` — what's left from this cycle's allowance.
 *     Resets on the first of each month (UTC) via the daily cron.
 *   * ``purchased_balance`` — credit-pack purchases. Never expires.
 *   * ``total_available``  — ``monthly_remaining + purchased_balance``.
 *     The number the command bar gates against.
 *   * ``reset_date``       — ISO date (YYYY-MM-01) of the next cycle.
 */
export interface CreditBalance {
  monthly_allowance: number;
  monthly_used: number;
  monthly_remaining: number;
  purchased_balance: number;
  total_available: number;
  reset_date: string;
  plan: string;
  is_internal: boolean;
}

/** One row in the credit-transactions ledger. ``amount`` is the
 *  signed delta (negative for usage, positive for purchases /
 *  resets); ``balance_after`` snapshots the total available right
 *  after this entry landed. */
export interface CreditTransaction {
  id: string;
  type: "usage" | "purchase" | "monthly_reset" | "adjustment" | string;
  amount: number;
  balance_after: number;
  description: string | null;
  command_text: string | null;
  created_at: string | null;
}

/** Catalog entry for a purchasable credit pack. ``available`` flips
 *  to False when the operator hasn't wired up the matching Stripe
 *  price id — the SPA disables the "Buy" button for that pack. */
export interface CreditPack {
  key: string;
  credits: number;
  price_usd: number;
  label: string;
  popular: boolean;
  available: boolean;
}

export async function getCreditBalance(): Promise<CreditBalance> {
  const res = await client.get<CreditBalance>("/billing/credits");
  return res.data;
}

export async function getCreditTransactions(
  limit = 10,
): Promise<CreditTransaction[]> {
  const res = await client.get<CreditTransaction[]>(
    "/billing/credits/transactions",
    { params: { limit } },
  );
  return res.data;
}

export async function getCreditPacks(): Promise<CreditPack[]> {
  const res = await client.get<CreditPack[]>("/billing/credits/packs");
  return res.data;
}

export interface CreditCheckoutResponse {
  checkout_url: string | null;
  session_id: string | null;
  credits: number;
  pack: string;
}

/** Start a Stripe Checkout session for a credit pack. SUPER_ADMIN
 *  only — backend enforces via ``MANAGE_BILLING``. SPA redirects
 *  the user to ``checkout_url`` on success. */
export async function startCreditCheckout(
  pack: string,
): Promise<CreditCheckoutResponse> {
  const res = await client.post<CreditCheckoutResponse>(
    "/billing/credits/checkout",
    { pack },
  );
  return res.data;
}
